import { useState, useRef } from "react";
import { MagnifyingGlass, Sparkle, X } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";

export const AISearch = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  const runSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setOpen(true);
    try {
      const { data } = await api.post("/search/ai", { query });
      setResults(data);
    } catch {
      setResults({ interpretation: {}, projects: [], tasks: [] });
    } finally {
      setLoading(false);
    }
  };

  const close = () => { setOpen(false); setResults(null); setQuery(""); };
  const closeMobile = () => { close(); setMobileOpen(false); };

  const resultsPanel = (
    <div data-testid="ai-search-results" className="card-flat p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="label-caps">AI Search Results</span>
        <button data-testid="ai-search-close-btn" onClick={close}><X size={14} /></button>
      </div>
      {loading && <div className="text-sm font-mono text-muted-foreground p-2">&gt; interpreting query...</div>}
      {!loading && results && (
        <div className="space-y-3">
          {results.projects?.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-1">Projects</div>
              {results.projects.map((p) => (
                <div key={p.id} data-testid={`ai-result-project-${p.id}`} onClick={() => { navigate(`/projects/${p.id}`); closeMobile(); }}
                  className="px-2 py-2 hover:bg-muted cursor-pointer text-sm border-b border-border">
                  {p.name} <span className="text-xs text-muted-foreground">· {p.status}</span>
                </div>
              ))}
            </div>
          )}
          {results.tasks?.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-1">Tasks</div>
              {results.tasks.map((t) => (
                <div key={t.id} data-testid={`ai-result-task-${t.id}`} onClick={() => { navigate(`/tasks`); closeMobile(); }}
                  className="px-2 py-2 hover:bg-muted cursor-pointer text-sm border-b border-border">
                  {t.title} <span className="text-xs text-muted-foreground">· {t.project_name} · {t.status}</span>
                </div>
              ))}
            </div>
          )}
          {(!results.projects?.length && !results.tasks?.length) && (
            <div className="text-sm font-mono text-muted-foreground">&gt; no matches found</div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Desktop / tablet: inline search box in the header */}
      <div className="relative hidden sm:block" ref={boxRef}>
        <form onSubmit={runSearch} className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="ai-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ask AI: overdue QC tasks for TV mounts…"
            className="w-full h-9 pl-9 pr-9 text-base bg-muted border border-border focus:outline-none focus:ring-1 focus:ring-accent"
            style={{ borderRadius: "2px" }}
          />
          <Sparkle size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-accent" />
        </form>
        {open && <div className="absolute mt-2 w-[420px] max-h-96 overflow-y-auto z-30">{resultsPanel}</div>}
      </div>

      {/* Mobile: icon-only trigger that opens a full-width search overlay */}
      <button
        data-testid="ai-search-mobile-trigger"
        onClick={() => setMobileOpen(true)}
        className="sm:hidden p-1.5 transition-colors hover:bg-[hsl(var(--header-fg)/0.1)]"
        style={{ borderRadius: "2px", border: "1px solid hsl(var(--header-fg) / 0.2)", color: "hsl(var(--header-fg))" }}
      >
        <MagnifyingGlass size={17} />
      </button>
      {mobileOpen && (
        <div className="sm:hidden fixed inset-0 z-50 bg-background" data-testid="ai-search-mobile-overlay">
          <div className="p-3 border-b border-border">
            <form onSubmit={runSearch} className="relative flex items-center gap-2">
              <div className="relative flex-1">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  data-testid="ai-search-mobile-input"
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Ask AI: overdue QC tasks…"
                  className="w-full h-10 pl-9 pr-9 text-base bg-muted border border-border focus:outline-none focus:ring-1 focus:ring-accent"
                  style={{ borderRadius: "2px" }}
                />
                <Sparkle size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-accent" />
              </div>
              <button data-testid="ai-search-mobile-close-btn" type="button" onClick={closeMobile} className="p-2 shrink-0"><X size={20} /></button>
            </form>
          </div>
          <div className="p-3">
            {open ? resultsPanel : <div className="text-sm font-mono text-muted-foreground p-2">&gt; type a query and press enter</div>}
          </div>
        </div>
      )}
    </>
  );
};
